import { definedMap } from "@glideapps/ts-necessities";
import { asString, isLoadedDefined, loadedDefinedMap } from "common/components/component-helpers";
import { getLocalizedString } from "common/components/localization";
import type { EvaluatedScreen, ForceValueProducer, RenderEnvironment } from "common/components/types";
import { asUI, LOADING, QuotaBannerMessage } from "common/components/types";
import { renderValueProducer, SubscribingReactComponent } from "common/components/uis";
import { withNetworkStatus } from "hooks/use-network-status";
import { withTheme } from "styled-components";
import { pushShareScreen } from "webapp/lib/share-utils";
import type { AppBarContext } from "../../../lib/app-utility-context";
import { SCROLL_CHANGED } from "../../../lib/constants";
import type { MenuItem } from "../../../lib/menu-item";
import { getMenuItemValues } from "../../../lib/menu-item";
import type { RuntimeTheme } from "../../../lib/theme/theme";
import OfflineBanner from "../../views/offline-banner/offline-banner";
import type { ModalController } from "../app-modal-provider/app-modal-provider";
import { withModals } from "../app-modal-provider/app-modal-provider";
import CollapseBar from "../collapse-bar/collapse-bar";
import { LargeTitleStyle } from "../collapse-bar/collapse-bar-style";
import NavBar from "../nav-bar/nav-bar";
import type { ScrollInfo } from "../scroll-reactive-component/scroll-reactive-component";
import { scrollIDForScreenID } from "../scroll-reactive-component/scroll-reactive-component";
import AppBarBackground from "./app-bar-background";
import { NavBarWrapper, PositionWrapper, QuotaBar } from "./glide-app-bar-style";

interface Props {
    isSubScreen: boolean;
    renderEnv: RenderEnvironment;
    appTitle: string;
    isOnFirstTab: boolean;
    showShareButton: boolean;
    screen: EvaluatedScreen;
    theme: RuntimeTheme;
    overrideContext: AppBarContext | undefined;
    onMenuClicked?: () => void;
    modals: ModalController;
    onLine: boolean;
}

interface State {
    isScrolledPastThreshold: boolean;
}

class GlideAppBar extends SubscribingReactComponent<Props, State> {
    private token: any;

    constructor(props: Props) {
        super(props);

        this.token = PubSub.subscribe(SCROLL_CHANGED, this.onScrollChanged);
    }

    // protected debugPrint(...items: any[]): void {
    //     logInfo(...items);
    // }

    public componentWillUnmount(): void {
        PubSub.unsubscribe(this.token);
        super.componentWillUnmount();
    }

    private onScrollChanged = (_msg: string, scrollInfo: ScrollInfo) => {
        const { screen } = this.props;
        const { id, position } = scrollInfo;

        // We only want to subscribe the screen we are scrolling
        if (scrollIDForScreenID(screen.screenId) !== id) return;

        const largeTitleHeight = this.props.theme.navBar.largeTitleHeight;
        if (largeTitleHeight > 0 && position >= largeTitleHeight) {
            if (!this.state.isScrolledPastThreshold) {
                this.setState({ isScrolledPastThreshold: true });
            }
        } else {
            if (this.state.isScrolledPastThreshold) {
                this.setState({ isScrolledPastThreshold: false });
            }
        }
    };

    public expandState(_props: Props, oldState: State | null): State {
        if (oldState !== null) return oldState;

        return { isScrolledPastThreshold: false };
    }

    private onShareClicked = async (): Promise<void> => {
        const { renderEnv } = this.props;
        pushShareScreen(renderEnv);
    };

    private getQuotaMessage(message: QuotaBannerMessage) {
        const { appKind } = this.props.renderEnv.appEnvironment;
        if (message === QuotaBannerMessage.List) {
            return getLocalizedString("listIsFull", appKind);
        }
        return getLocalizedString("mapIsFull", appKind);
    }

    public recompute(force: ForceValueProducer): React.ReactNode {
        const props = this.props;
        const { renderEnv, screen, theme, overrideContext, onLine } = props;
        const appEnv = renderEnv.appEnvironment;
        const { appKind } = appEnv;
        const ctx = screen.ctx;
        const topBar = screen.topBar;
        const pivot = screen.pivotPicker;

        let onBack: (() => void) | undefined;
        if (overrideContext !== undefined) {
            onBack = overrideContext.onBack;
        } else if (props.isSubScreen) {
            onBack = () => appEnv.popScreen(renderEnv);
        }

        let title = definedMap(screen.title, force);
        if (overrideContext !== undefined) {
            title = overrideContext.title;
        } else if (title === LOADING) {
            title = getLocalizedString("loading", appKind);
        } else if (title === undefined) {
            title = "";
        }

        let barButtonItems: MenuItem[] = [];
        if (overrideContext !== undefined) {
            barButtonItems = overrideContext.barButtonItems;
        } else if (screen.menuItems !== undefined) {
            for (const m of screen.menuItems) {
                const item = getMenuItemValues(force, m, ctx, renderEnv);
                if (item === undefined) continue;

                barButtonItems.push(item);
            }
        }

        if (props.isOnFirstTab && this.props.showShareButton) {
            barButtonItems.push({
                title: getLocalizedString("share", appKind),
                icon: "00-01-glide-info",
                style: "share",
                onClick: this.onShareClicked,
            });
        }

        if (props.onMenuClicked !== undefined) {
            barButtonItems.push({
                title: "menu", // FIXME: Should be localized even though its never displayed
                icon: "00-01-glide-menu",
                style: "menu",
                onClick: props.onMenuClicked,
            });
        }

        const isInline = theme.searchBar.style === "Inline";
        const searchBar = renderValueProducer(topBar, force, ctx, renderEnv, true);
        const pivotBar = renderValueProducer(pivot, force, ctx, renderEnv, false);

        let showTitle = this.state.isScrolledPastThreshold;
        if (this.props.theme.navBar.largeTitleHeight === 0) {
            showTitle = true;
        }

        const titleStr = asString(title);

        const quotaBannerMessage = force(screen.quotaBannerMessage);
        let message: QuotaBannerMessage | false = false;
        const useBanner = true;
        if (isLoadedDefined(quotaBannerMessage) && useBanner) {
            message = quotaBannerMessage as QuotaBannerMessage;
        }

        const isTablet = renderEnv.sizeClass === "tablet";
        const inTabletModal = isTablet && renderEnv.displayContext === "modal";
        const isTabletOrBuilder = isTablet || appEnv.isBuilder;

        return (
            <NavBarWrapper
                data-test="glide-app-bar"
                tabletOrBuilder={isTabletOrBuilder}
                hasSearchBar={isInline && searchBar !== undefined}
                hasPivot={pivot !== undefined}
                hasQuotaBar={message !== false}
                inTabletModal={inTabletModal}
                hasOfflineBanner={!onLine}>
                <AppBarBackground screen={screen} />
                <CollapseBar screen={screen} collapser={<LargeTitleStyle>{titleStr}</LargeTitleStyle>}>
                    {isInline && searchBar}
                    {pivotBar}
                    {appEnv.eminenceFlags.showQuotaBanners && message !== false && (
                        <QuotaBar>{this.getQuotaMessage(message)}</QuotaBar>
                    )}
                    <OfflineBanner />
                </CollapseBar>
                <PositionWrapper>
                    <NavBar
                        showTitle={showTitle}
                        ctx={ctx}
                        renderEnvironment={renderEnv}
                        barButtonItems={barButtonItems}
                        onBack={onBack}
                        title={titleStr}
                        searchBar={
                            isInline ? undefined : definedMap(topBar, vp => loadedDefinedMap(force(vp), s => asUI(s)))
                        }
                    />
                </PositionWrapper>
            </NavBarWrapper>
        );
    }
}

export default withNetworkStatus(withModals(withTheme(GlideAppBar)));
